import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useCMSPageField } from '@/cms/fields'
import { groupBookingsAPIClient } from '@/infrastructure/groups/api'
import { Custom500 } from '@/pages/500'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import { CheckoutHeading, CheckoutHeadingTitle } from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { Loader, MainContent } from '@/scopes/checkout/components/Layout'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid, useCheckoutSidSession } from '@/scopes/checkout/domain/hooks'
import { CheckoutSummary } from '@/scopes/checkout/groups/components/CheckoutSummary'
import { Content, MainContentCentered } from '@/scopes/checkout/groups/components/MainContentCentered'
import {
  PageLayout,
} from '@/scopes/checkout/groups/components/PageLayout'
import { GroupsDependenciesProvider } from '@/scopes/checkout/groups/dependencies/context'
import { checkoutAPIClient } from '@/scopes/checkout/infrastructure/api'
import { CheckoutGroupsNotificationError } from '@/scopes/checkout/pages/groups/Components/NotificationError'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import createNextRouter from '@/scopes/common/infrastructure/router/nextRouter'
import styles from './pages.module.css'

const queryClient = new QueryClient()

function Page() {
  const sid = useCheckoutSid()
  const { isLoading, data } = useBookingDraftQuery()
  const intl = useIntl()
  const { hasChecked } = useCheckoutSidSession()
  const brandName = useConfiguration('brandName')

  useSendCheckoutEvent()

  if (!hasChecked) {
    return (
      <PageLayout>
        <Loader />
      </PageLayout>
    )
  }

  if (!sid) {
    return (
      <PageLayout>
        <CheckoutGroupsNotificationError />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      {data?.error && data.error.hasError && <CheckoutError showRedirectButton />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <NewRelicErrorBoundary scope="groups-page" fallback={<Custom500 brand={brandName} />}>
          <CheckoutHeader showLoginMenu={false} />
          <MainContent>
            {isLoading
              ? (
                  <Loader />
                )
              : (
                  <div className={cn(styles.flexContent, styles.flexContentGap12)}>
                    <CheckoutProgress />
                    <MainContentCentered>
                      <CheckoutHeading>
                        <CheckoutHeadingTitle>
                          {intl.formatMessage({ id: 'groups_checkout_steps_header_overview_step_title' })}
                        </CheckoutHeadingTitle>
                      </CheckoutHeading>
                      <Content>
                        <CheckoutSummary />
                        <CheckoutSection title="">
                          <Typography
                            variant="body-m-regular-tight"
                            dangerouslySetInnerHTML={{
                              __html: intl.formatMessage({ id: 'groups_checkout_steps_overview_step_conditions' }),
                            }}
                          />
                        </CheckoutSection>

                        <CheckoutNextStep />
                      </Content>
                    </MainContentCentered>
                  </div>
                )}
          </MainContent>
        </NewRelicErrorBoundary>
      )}
    </PageLayout>
  )
}

export default function CheckoutPageOverview() {
  const contextItemId = useCMSPageField('contextItemId')
  const router = useRouter()
  const [sid, setSid] = React.useState<string | null>(null)
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    draftId: sid || null,
    setDraftId: setSid,
    step: 3,
  }), [contextItemId, sid])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <GroupsDependenciesProvider
          dependencies={{ checkoutAPIClient, groupBookingsAPIClient, router: createNextRouter(router) }}
        >
          <Page />
          <DevTools />
        </GroupsDependenciesProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
